import { EXTRA_PURCHASE_FIELDS } from './utils'
import type { ICustomField, IFieldBind } from './modals/CustomExtraFieldModal/types'

export const EXTRA_PURCHASE_FIELD_BINDINGS_KEY = 'extraPurchaseFieldBindings'

export type ExtraPurchaseFieldBinding = {
  key: string
  children_field_id: string | null
  parent_field_id: string | null
  crm_lead_field_id: string | null
  children_field_id_changeable: boolean
  parent_field_id_changeable: boolean
  crm_lead_field_id_changeable: boolean
  /** Заменять значение в карточке лида, если при покупке введены другие данные */
  replace_crm_lead_value: boolean
}

type SystemBindingDefault = Omit<ExtraPurchaseFieldBinding, 'crm_lead_field_id'> & {
  crm_lead_field_unique_name: string | null
}

/** Дефолты system-полей: жёсткие связи с анкетами + CRM unique_name */
export const SYSTEM_FIELD_BINDING_DEFAULTS: Record<string, SystemBindingDefault> =
  {
    studentName: {
      key: 'studentName',
      children_field_id: 'name',
      parent_field_id: null,
      crm_lead_field_unique_name: 'NAME',
      children_field_id_changeable: false,
      parent_field_id_changeable: true,
      crm_lead_field_id_changeable: false,
      replace_crm_lead_value: false,
    },
    studentEmail: {
      key: 'studentEmail',
      children_field_id: 'email',
      parent_field_id: null,
      crm_lead_field_unique_name: 'EMAIL',
      children_field_id_changeable: false,
      parent_field_id_changeable: true,
      crm_lead_field_id_changeable: false,
      replace_crm_lead_value: false,
    },
    studentPhone: {
      key: 'studentPhone',
      children_field_id: 'phone',
      parent_field_id: null,
      crm_lead_field_unique_name: 'PHONE',
      children_field_id_changeable: false,
      parent_field_id_changeable: true,
      crm_lead_field_id_changeable: false,
      replace_crm_lead_value: false,
    },
    password: {
      key: 'password',
      children_field_id: null,
      parent_field_id: null,
      crm_lead_field_unique_name: null,
      children_field_id_changeable: false,
      parent_field_id_changeable: false,
      crm_lead_field_id_changeable: false,
      replace_crm_lead_value: false,
    },
    parentName: {
      key: 'parentName',
      children_field_id: null,
      parent_field_id: 'name',
      crm_lead_field_unique_name: 'PARENTNAME',
      children_field_id_changeable: true,
      parent_field_id_changeable: false,
      crm_lead_field_id_changeable: false,
      replace_crm_lead_value: false,
    },
    parentPhone: {
      key: 'parentPhone',
      children_field_id: null,
      parent_field_id: 'phone',
      crm_lead_field_unique_name: 'PARENTPHONE',
      children_field_id_changeable: true,
      parent_field_id_changeable: false,
      crm_lead_field_id_changeable: false,
      replace_crm_lead_value: false,
    },
    parentEmail: {
      key: 'parentEmail',
      children_field_id: null,
      parent_field_id: 'email',
      crm_lead_field_unique_name: 'PARENTEMAIL',
      children_field_id_changeable: true,
      parent_field_id_changeable: false,
      crm_lead_field_id_changeable: false,
      replace_crm_lead_value: false,
    },
    parentPassword: {
      key: 'parentPassword',
      children_field_id: null,
      parent_field_id: null,
      crm_lead_field_unique_name: null,
      children_field_id_changeable: false,
      parent_field_id_changeable: false,
      crm_lead_field_id_changeable: false,
      replace_crm_lead_value: false,
    },
  }

export type QuestionnaireFieldOption = {
  id: string
  name: string
  /**
   * Тип поля из questionnaire (profileFields).
   * Используется для совместимости типов в настройках.
   */
  type?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const toNullableString = (value: unknown): string | null => {
  if (value == null || value === '') return null
  return String(value)
}

const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value
  if (value === '1' || value === 1) return true
  if (value === '0' || value === 0) return false
  return fallback
}

export const createEmptyCustomBinding = (
  key: string,
): ExtraPurchaseFieldBinding => ({
  key,
  children_field_id: null,
  parent_field_id: null,
  crm_lead_field_id: null,
  children_field_id_changeable: true,
  parent_field_id_changeable: true,
  crm_lead_field_id_changeable: true,
  replace_crm_lead_value: false,
})

const bindingFromLegacyCustomBinds = (
  key: string,
  binds: IFieldBind[] = [],
): ExtraPurchaseFieldBinding => {
  const base = createEmptyCustomBinding(key)
  let children_field_id = base.children_field_id
  let parent_field_id = base.parent_field_id
  let crm_lead_field_id = base.crm_lead_field_id

  binds.forEach((bind) => {
    if (bind.type === 'crm') {
      crm_lead_field_id = bind.id
      return
    }

    // Старый bind type 'profile' смешивал ученика и родителя
    if (bind.id === 'parent_phone' || bind.id.startsWith('parent')) {
      parent_field_id = bind.id === 'parent_phone' ? 'phone' : bind.id
      return
    }

    children_field_id = bind.id
  })

  return {
    ...base,
    children_field_id,
    parent_field_id,
    crm_lead_field_id,
  }
}

const normalizeBinding = (
  value: unknown,
  fallback: ExtraPurchaseFieldBinding,
): ExtraPurchaseFieldBinding => {
  if (!isRecord(value)) return fallback

  return {
    key: String(value.key ?? fallback.key),
    children_field_id:
      value.children_field_id === undefined
        ? fallback.children_field_id
        : toNullableString(value.children_field_id),
    parent_field_id:
      value.parent_field_id === undefined
        ? fallback.parent_field_id
        : toNullableString(value.parent_field_id),
    crm_lead_field_id:
      value.crm_lead_field_id === undefined
        ? fallback.crm_lead_field_id
        : toNullableString(value.crm_lead_field_id),
    children_field_id_changeable: toBoolean(
      value.children_field_id_changeable,
      fallback.children_field_id_changeable,
    ),
    parent_field_id_changeable: toBoolean(
      value.parent_field_id_changeable,
      fallback.parent_field_id_changeable,
    ),
    crm_lead_field_id_changeable: toBoolean(
      value.crm_lead_field_id_changeable,
      fallback.crm_lead_field_id_changeable,
    ),
    replace_crm_lead_value: toBoolean(
      value.replace_crm_lead_value,
      fallback.replace_crm_lead_value,
    ),
  }
}

const systemDefaultToBinding = (
  defaults: SystemBindingDefault,
  crmUniqueNameToId: Record<string, string>,
): ExtraPurchaseFieldBinding => {
  const { crm_lead_field_unique_name, ...rest } = defaults
  return {
    ...rest,
    crm_lead_field_id: crm_lead_field_unique_name
      ? crmUniqueNameToId[crm_lead_field_unique_name] ?? null
      : null,
  }
}

export const parseExtraPurchaseFieldBindings = (
  raw: unknown,
): ExtraPurchaseFieldBinding[] => {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((item) => isRecord(item) && item.key)
      .map((item) =>
        normalizeBinding(item, createEmptyCustomBinding(String(item.key))),
      )
  } catch (error) {
    console.error(error)
    return []
  }
}

/**
 * Собирает полный список bindings для всех system + custom полей.
 * Если 4-го ключа нет — сидирует из дефолтов и legacy bind кастомных полей.
 */
export const buildExtraPurchaseFieldBindings = ({
  storedBindings,
  customFields,
  crmFields = [],
}: {
  storedBindings: ExtraPurchaseFieldBinding[]
  customFields: ICustomField[]
  crmFields?: Array<{ id: number | string; unique_name?: string }>
}): ExtraPurchaseFieldBinding[] => {
  const crmUniqueNameToId = crmFields.reduce<Record<string, string>>(
    (acc, field) => {
      if (field.unique_name) {
        acc[field.unique_name] = String(field.id)
      }
      return acc
    },
    {},
  )

  const byKey = new Map(
    storedBindings.map((binding) => [binding.key, binding] as const),
  )

  const systemBindings = EXTRA_PURCHASE_FIELDS.map(({ key }) => {
    const defaults = SYSTEM_FIELD_BINDING_DEFAULTS[key]
    const fallback = defaults
      ? systemDefaultToBinding(defaults, crmUniqueNameToId)
      : createEmptyCustomBinding(key)
    const stored = byKey.get(key)
    if (!stored) return fallback

    // Флаги changeable всегда берём из дефолтов system-поля
    return {
      ...normalizeBinding(stored, fallback),
      children_field_id_changeable: fallback.children_field_id_changeable,
      parent_field_id_changeable: fallback.parent_field_id_changeable,
      crm_lead_field_id_changeable: fallback.crm_lead_field_id_changeable,
      // Неизменяемые id не даём перезаписать пустотой из старых данных
      children_field_id: fallback.children_field_id_changeable
        ? normalizeBinding(stored, fallback).children_field_id
        : fallback.children_field_id,
      parent_field_id: fallback.parent_field_id_changeable
        ? normalizeBinding(stored, fallback).parent_field_id
        : fallback.parent_field_id,
      crm_lead_field_id: fallback.crm_lead_field_id_changeable
        ? normalizeBinding(stored, fallback).crm_lead_field_id
        : fallback.crm_lead_field_id ??
          normalizeBinding(stored, fallback).crm_lead_field_id,
    }
  })

  const customBindings = customFields.map((field) => {
    const stored = byKey.get(field.key)
    if (stored) return normalizeBinding(stored, createEmptyCustomBinding(field.key))
    return bindingFromLegacyCustomBinds(field.key, field.bind)
  })

  return [...systemBindings, ...customBindings]
}

export const upsertBinding = (
  bindings: ExtraPurchaseFieldBinding[],
  next: ExtraPurchaseFieldBinding,
): ExtraPurchaseFieldBinding[] => {
  const index = bindings.findIndex((item) => item.key === next.key)
  if (index === -1) return [...bindings, next]
  return bindings.map((item, i) => (i === index ? next : item))
}

export const removeBinding = (
  bindings: ExtraPurchaseFieldBinding[],
  key: string,
): ExtraPurchaseFieldBinding[] => bindings.filter((item) => item.key !== key)

export const getBindingLabel = (
  fieldId: string | null | undefined,
  options: QuestionnaireFieldOption[],
): string => {
  if (!fieldId) return '—'
  return options.find((option) => option.id === fieldId)?.name ?? fieldId
}

export type BindingDestinationKind = 'children' | 'parent' | 'crm'

/** fieldId → название поля поп-апа, которое уже заняло этот destination */
export const buildOccupiedDestinationMap = (
  bindings: ExtraPurchaseFieldBinding[],
  kind: BindingDestinationKind,
  excludeKey?: string | null,
  fieldLabels: Record<string, string> = {},
): Record<string, string> => {
  const occupied: Record<string, string> = {}

  bindings.forEach((binding) => {
    if (excludeKey && binding.key === excludeKey) return

    const fieldId =
      kind === 'children'
        ? binding.children_field_id
        : kind === 'parent'
          ? binding.parent_field_id
          : binding.crm_lead_field_id

    if (!fieldId || occupied[fieldId]) return

    occupied[fieldId] =
      fieldLabels[binding.key] ??
      EXTRA_PURCHASE_FIELDS.find((field) => field.key === binding.key)?.label ??
      binding.key
  })

  return occupied
}

export const hasBindingDestinationConflict = (
  candidate: ExtraPurchaseFieldBinding,
  activeBindings: ExtraPurchaseFieldBinding[],
): boolean => {
  const used = new Set<string>()

  activeBindings.forEach((binding) => {
    if (binding.key === candidate.key) return
    if (binding.children_field_id) {
      used.add(`children:${binding.children_field_id}`)
    }
    if (binding.parent_field_id) {
      used.add(`parent:${binding.parent_field_id}`)
    }
    if (binding.crm_lead_field_id) {
      used.add(`crm:${binding.crm_lead_field_id}`)
    }
  })

  return (
    (!!candidate.children_field_id &&
      used.has(`children:${candidate.children_field_id}`)) ||
    (!!candidate.parent_field_id &&
      used.has(`parent:${candidate.parent_field_id}`)) ||
    (!!candidate.crm_lead_field_id &&
      used.has(`crm:${candidate.crm_lead_field_id}`))
  )
}
